import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'atoms/Snackbar';
import { fetchGCPBillingData, resendGCPBillingInvoice } from './api';
import { buildGCPBillingQuery } from './query';
import {
  GCPBillingCompanyFilter,
  GCPBillingDataRow,
  GCPBillingFilters,
  GCPBillingInvoiceFilter,
  GCPBillingPageSize,
  GCPBillingPagination,
} from './types';

const DEFAULT_FILTERS: GCPBillingFilters = {
  month: null,
  gcpBillingId: '',
  currencies: [],
  invoiceFilter: 'all',
  companyFilter: 'all',
};

const DEFAULT_PAGINATION: GCPBillingPagination = {
  page: 1,
  perPage: 20,
};

type GCPBillingQueryState = {
  debouncedBillingId: string;
  pagination: GCPBillingPagination;
};

type GCPBillingApiError = {
  response?: { data?: { message?: string; error?: string } };
  message?: string;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as GCPBillingApiError;

  return (
    apiError.response?.data?.message ||
    apiError.response?.data?.error ||
    apiError.message ||
    fallback
  );
};

export const useGCPBillingOperations = () => {
  const { showSnackbar } = useSnackbar();
  const [filters, setFilters] = useState<GCPBillingFilters>(DEFAULT_FILTERS);
  const [queryState, setQueryState] = useState<GCPBillingQueryState>({
    debouncedBillingId: '',
    pagination: DEFAULT_PAGINATION,
  });
  const { debouncedBillingId, pagination } = queryState;
  const [rows, setRows] = useState<GCPBillingDataRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [pendingResendRow, setPendingResendRow] =
    useState<GCPBillingDataRow | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const invalidateListRequests = useCallback(() => {
    requestVersion.current += 1;
  }, []);

  const loadList = useCallback(async () => {
    requestVersion.current += 1;
    const version = requestVersion.current;
    const query = buildGCPBillingQuery(
      {
        month: filters.month,
        gcpBillingId: debouncedBillingId,
        currencies: filters.currencies,
        invoiceFilter: filters.invoiceFilter,
        companyFilter: filters.companyFilter,
      },
      pagination
    );

    setLoading(true);
    setListError(null);

    try {
      const response = await fetchGCPBillingData(query);
      if (version !== requestVersion.current) return;

      setRows(response.rows);
      setTotal(response.meta.total_count);
    } catch (error) {
      if (version !== requestVersion.current) return;

      setListError(getErrorMessage(error, 'Unable to load GCP billing data.'));
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [
    debouncedBillingId,
    filters.companyFilter,
    filters.currencies,
    filters.invoiceFilter,
    filters.month,
    pagination,
  ]);
  const latestLoadList = useRef(loadList);
  latestLoadList.current = loadList;

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setQueryState((current) => {
        const nextPagination =
          current.pagination.page === 1
            ? current.pagination
            : { ...current.pagination, page: 1 };

        if (
          current.debouncedBillingId === filters.gcpBillingId &&
          nextPagination === current.pagination
        ) {
          return current;
        }

        return {
          debouncedBillingId: filters.gcpBillingId,
          pagination: nextPagination,
        };
      });
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [filters.gcpBillingId]);

  const resetPage = useCallback(() => {
    setQueryState((current) =>
      current.pagination.page === 1
        ? current
        : {
            ...current,
            pagination: { ...current.pagination, page: 1 },
          }
    );
  }, []);

  const setMonth = useCallback(
    (value: string | null) => {
      invalidateListRequests();
      setFilters((current) => ({ ...current, month: value }));
      resetPage();
    },
    [invalidateListRequests, resetPage]
  );

  const setGCPBillingId = useCallback(
    (value: string) => {
      invalidateListRequests();
      setFilters((current) => ({ ...current, gcpBillingId: value }));
    },
    [invalidateListRequests]
  );

  const setCurrencies = useCallback(
    (value: string[]) => {
      invalidateListRequests();
      setFilters((current) => ({ ...current, currencies: value }));
      resetPage();
    },
    [invalidateListRequests, resetPage]
  );

  const setInvoiceFilter = useCallback(
    (value: GCPBillingInvoiceFilter) => {
      invalidateListRequests();
      setFilters((current) => ({ ...current, invoiceFilter: value }));
      resetPage();
    },
    [invalidateListRequests, resetPage]
  );

  const setCompanyFilter = useCallback(
    (value: GCPBillingCompanyFilter) => {
      invalidateListRequests();
      setFilters((current) => ({ ...current, companyFilter: value }));
      resetPage();
    },
    [invalidateListRequests, resetPage]
  );

  const setPage = useCallback(
    (value: number) => {
      invalidateListRequests();
      setQueryState((current) => ({
        ...current,
        pagination: { ...current.pagination, page: value },
      }));
    },
    [invalidateListRequests]
  );

  const setPerPage = useCallback(
    (value: GCPBillingPageSize) => {
      invalidateListRequests();
      setQueryState((current) => ({
        ...current,
        pagination: { page: 1, perPage: value },
      }));
    },
    [invalidateListRequests]
  );

  const retryList = useCallback(() => {
    loadList();
  }, [loadList]);

  const openResend = useCallback((row: GCPBillingDataRow) => {
    setPendingResendRow(row);
  }, []);

  const closeResend = useCallback(() => {
    if (!resendingId) setPendingResendRow(null);
  }, [resendingId]);

  const confirmResend = useCallback(async () => {
    if (!pendingResendRow) return;

    setResendingId(pendingResendRow.id);

    try {
      const response = await resendGCPBillingInvoice(pendingResendRow.id);
      setRows((current) =>
        current.map((row) => (row.id === response.row.id ? response.row : row))
      );
      showSnackbar(
        response.message || 'Invoice creation triggered successfully.',
        'success'
      );
      setPendingResendRow(null);
      setResendingId(null);
      await latestLoadList.current();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, 'Unable to resend GCP billing invoice.'),
        'error'
      );
      setResendingId(null);
    }
  }, [pendingResendRow, showSnackbar]);

  return {
    filters,
    pagination,
    rows,
    total,
    loading,
    listError,
    pendingResendRow,
    resendingId,
    setMonth,
    setGCPBillingId,
    setCurrencies,
    setInvoiceFilter,
    setCompanyFilter,
    setPage,
    setPerPage,
    retryList,
    openResend,
    closeResend,
    confirmResend,
  };
};
